import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import React, { useCallback } from 'react';
import { useRemoveUserFromProject } from './useRemoveUserFromProject';

type UseConfirmRemoveUserFromProjectParams = {
  projectId: string;
  userId: string;
  userName: string;
};

export function useConfirmRemoveUserFromProject({
  projectId,
  userId,
  userName,
}: UseConfirmRemoveUserFromProjectParams) {
  const { confirm } = useModal();
  const { showUserFriendlyErrorToast, success } = useToast();
  const [removeUserFromProject] = useRemoveUserFromProject({
    config: { throwOnError: true },
  });

  const handleRemoveConfirm = useCallback(
    async (isConfirmed: boolean) => {
      if (isConfirmed) {
        await removeUserFromProject({ projectId, userId });
      }
    },
    [projectId, removeUserFromProject, userId],
  );

  return useCallback(async () => {
    try {
      const isConfirmed = await confirm({
        title: 'Remove from site',
        description: (
          <>
            Are you sure you want to remove <b>{userName}</b> from this site?
          </>
        ),
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Remove from site',
        afterConfirmButtonText: 'Removing...',
        danger: true,
        onConfirmAsync: handleRemoveConfirm,
      });
      if (isConfirmed) {
        success(`${userName} has been removed from this site.`);
      }
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        `Unable to remove ${userName} from this site.`,
      );
    }
  }, [
    confirm,
    handleRemoveConfirm,
    showUserFriendlyErrorToast,
    success,
    userName,
  ]);
}
